import React, { useContext, useRef, useEffect, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import classes from './TabNav.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import BackButton from '@/components/BackButton/BackButton';
import Skeleton from 'react-loading-skeleton';
import { isEmpty } from 'underscore';
import { viewportContext } from '@/context/viewport.context';
import { subscribeToTranslationUpdates, getTranslationsSnapshot } from '@/utils/Language';

const TabNav = ({
  tabs,
  t,
  currentTab,
  withBackButton,
  setCurrentTab,
  contentIsLoading,
  title,
  alignTitle,
  subTitle,
  icon,
  parentPage,
  parentClass,
  showTitleContainer = true
}) => {
  const { isMobileOrTablet } = useContext(viewportContext);
  useSyncExternalStore(subscribeToTranslationUpdates, getTranslationsSnapshot, () => 0);
  const tabsWrpr = useRef();
  const resizeTimer = useRef();

  const changeTab = (tab) => {
    setCurrentTab(tab.key);
  };

  const titleElement = () => {
    if (typeof title === 'string') {
      return (
        <div className={classes.headerTitle}>
          <h1 className={classes.pageTitle}>
            {icon && (
              <>
                <FontAwesomeIcon icon={icon} />
                &nbsp;
              </>
            )}

            {isEmpty(title) && (
              <>
                <Skeleton height={25} width={150} />
              </>
            )}
            {!isEmpty(title) && <>{t(title)}</>}
          </h1>
          {subTitle && <p className={classes.pageSubTitleTitle}>{!isEmpty(subTitle) && <>{t(subTitle)}</>}</p>}
        </div>
      );
    } else if (typeof title === 'function') {
      return title();
    } else if (isEmpty(title)) {
      return '';
    } else {
      return <>{title}</>;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      // On mobile, scroll tabs into view only when they've been pushed below the viewport
      // (e.g. virtual keyboard opens and shifts content down). Never scroll up to tabs
      // the user has already scrolled past — that causes disorienting jumps on iOS where
      // the URL bar hide/show fires resize events while the user is scrolling.
      if (currentTab && isMobileOrTablet && typeof tabsWrpr.current?.scrollIntoView === 'function') {
        clearTimeout(resizeTimer.current);
        resizeTimer.current = setTimeout(() => {
          const rect = tabsWrpr.current.getBoundingClientRect();
          if (rect.top > window.innerHeight) {
            tabsWrpr.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [currentTab, isMobileOrTablet]);

  return (
    <div className={[classes.tabNav, parentClass ? parentClass : ''].join(' ')}>
      {showTitleContainer && (
        <div className={[classes.titleContainer, alignTitle ? classes[alignTitle] : ''].join(' ')}>
          <Container fluid={false}>
            <Row>
              <Col xs="12">
                {withBackButton && <BackButton additionalClasses={classes.backButton} />}
                {titleElement()}
              </Col>
            </Row>
          </Container>
        </div>
      )}
      <Container fluid={false}>
        <Row className={[classes.pageHeader, parentPage?.pageHeaderClass || ''].join(' ')}>
          <Col>
            <ul data-cy="nav-tabs" className={classes.tabs} ref={tabsWrpr}>
              {tabs.map((tab) => (
                <React.Fragment key={tab.key}>
                  {!tab.hidden && (
                    <li key={tab.title} data-key={tab.key}>
                      <a
                        onClick={() => changeTab(tab)}
                        className={`${classes.tabLink} ${currentTab?.key === tab.key ? [classes.active, tab.parentActiveClass].join(' ') : ''}`}
                      >
                        {tab.icon && (
                          <>
                            <FontAwesomeIcon icon={tab.icon} />
                            &nbsp;
                          </>
                        )}
                        {t(tab.title)}
                      </a>
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ul>
          </Col>
        </Row>
      </Container>
      <div className={[classes.tabContainer, parentClass ? parentClass : ''].join(' ')}>
        {contentIsLoading && <Skeleton height={500} highlightColor={'#ffffff'} />}
        {!contentIsLoading && (
          <div className={classes.tabContent}>{currentTab?.renderComponent ? currentTab.renderComponent() : currentTab?.Component}</div>
        )}
      </div>
    </div>
  );
};

TabNav.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      renderComponent: PropTypes.func,
      component: PropTypes.elementType,
      title: PropTypes.string,
      key: PropTypes.string
    })
  ),
  title: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.func])
};

export default TabNav;
